// Service for managing incident attachments in Supabase Storage
// CREATED: Handles file uploads, downloads, and deletion for incident attachments
// Features:
// - Upload files to Supabase Storage bucket
// - Generate signed URLs for secure file access
// - Delete files from storage and database
// - Client-side validation (file size, MIME type, count)

import { supabase } from './supabase';
import { captureException } from './sentry';

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
export const MAX_FILES_COUNT = 5;
export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/gif',
  'image/tiff',
  'application/pdf',
];

export interface IncidentAttachment {
  id: string;
  incident_id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  created_at: string;
}

export interface FileValidationError {
  file: File;
  error: string;
}

/**
 * Validates a file before upload
 */
export function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return `Ce fichier est trop volumineux pour être envoyé`;
  }

  // Accept all image types and PDFs (permissive approach for better UX)
  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';

  if (!isImage && !isPdf) {
    return `Ce type de fichier n'est pas supporté. Utilisez une photo ou un PDF.`;
  }

  return null;
}

/**
 * Validates multiple files
 */
export function validateFiles(files: File[]): {
  valid: File[];
  errors: FileValidationError[];
} {
  const valid: File[] = [];
  const errors: FileValidationError[] = [];

  if (files.length > MAX_FILES_COUNT) {
    return {
      valid: [],
      errors: [{ file: files[0], error: `Vous avez atteint le nombre maximum de fichiers` }],
    };
  }

  files.forEach((file) => {
    const error = validateFile(file);
    if (error) {
      errors.push({ file, error });
    } else {
      valid.push(file);
    }
  });

  return { valid, errors };
}

/**
 * Uploads an incident attachment to Supabase Storage and creates a database record
 */
export async function uploadIncidentAttachment(
  incidentId: string,
  file: File
): Promise<IncidentAttachment> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Utilisateur non authentifié');
  }

  // Validate file
  const validationError = validateFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  // Generate unique file path: {user_id}/{incident_id}/{timestamp}_{filename}
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `${user.id}/${incidentId}/${timestamp}_${sanitizedFileName}`;

  // Upload to storage
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('incident-attachments')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    if (import.meta.env.DEV) console.error('Error uploading file:', uploadError);
    captureException(uploadError, { context: 'uploadIncidentAttachment.upload', incidentId });
    throw new Error(`Erreur lors de l'upload du fichier: ${uploadError.message}`);
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('incident-attachments')
    .getPublicUrl(uploadData.path);

  // Insert record in database
  const { data: attachmentData, error: dbError } = await supabase
    .from('incident_attachments')
    .insert({
      incident_id: incidentId,
      file_url: uploadData.path, // Store the path, not the public URL
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
    })
    .select()
    .single();

  if (dbError) {
    // Rollback: delete the uploaded file
    await supabase.storage
      .from('incident-attachments')
      .remove([uploadData.path]);

    if (import.meta.env.DEV) console.error('Error creating attachment record:', dbError);
    captureException(dbError, { context: 'uploadIncidentAttachment.dbInsert', incidentId });
    throw new Error(`Erreur lors de l'enregistrement du fichier: ${dbError.message}`);
  }

  return attachmentData;
}

/**
 * Get all attachments for an incident
 */
export async function getIncidentAttachments(
  incidentId: string
): Promise<IncidentAttachment[]> {
  const { data, error } = await supabase
    .from('incident_attachments')
    .select('*')
    .eq('incident_id', incidentId)
    .order('created_at', { ascending: true });

  if (error) {
    if (import.meta.env.DEV) console.error('Error fetching attachments:', error);
    captureException(error, { context: 'getIncidentAttachments', incidentId });
    throw new Error(`Erreur lors de la récupération des pièces jointes: ${error.message}`);
  }

  return data || [];
}

/**
 * Delete an attachment (from both storage and database)
 */
export async function deleteIncidentAttachment(
  attachmentId: string
): Promise<void> {
  // Get attachment info first
  const { data: attachment, error: fetchError } = await supabase
    .from('incident_attachments')
    .select('file_url')
    .eq('id', attachmentId)
    .single();

  if (fetchError) {
    if (import.meta.env.DEV) console.error('Error fetching attachment:', fetchError);
    captureException(fetchError, { context: 'deleteIncidentAttachment.fetch', attachmentId });
    throw new Error(`Erreur lors de la récupération de la pièce jointe: ${fetchError.message}`);
  }

  if (!attachment) {
    throw new Error('Pièce jointe introuvable');
  }

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('incident-attachments')
    .remove([attachment.file_url]);

  if (storageError) {
    if (import.meta.env.DEV) console.error('Error deleting file from storage:', storageError);
    captureException(storageError, { context: 'deleteIncidentAttachment.storage', attachmentId });
    // Continue anyway to delete from database
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('incident_attachments')
    .delete()
    .eq('id', attachmentId);

  if (dbError) {
    if (import.meta.env.DEV) console.error('Error deleting attachment record:', dbError);
    captureException(dbError, { context: 'deleteIncidentAttachment.db', attachmentId });
    throw new Error(`Erreur lors de la suppression de la pièce jointe: ${dbError.message}`);
  }
}

/**
 * Generate a signed URL for temporary access (1 hour)
 */
export async function getSignedUrl(filePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('incident-attachments')
    .createSignedUrl(filePath, 3600); // 1 hour

  if (error) {
    if (import.meta.env.DEV) console.error('Error creating signed URL:', error);
    captureException(error, { context: 'getSignedUrl', filePath });
    throw new Error(`Erreur lors de la génération de l'URL: ${error.message}`);
  }

  return data.signedUrl;
}

/**
 * Get a public download URL for an attachment
 */
export function getPublicUrl(filePath: string): string {
  const { data } = supabase.storage
    .from('incident-attachments')
    .getPublicUrl(filePath);

  return data.publicUrl;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Check if a file is an image
 */
export function isImageFile(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Check if a file is a PDF
 */
export function isPdfFile(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}
